<script lang="ts">
  export * from './DataView.constants';
  export * from './DataView.keys';
  export * from './DataView.types';
  export * from './DataView.utils';
</script>

<script setup lang="ts">
  import { cloneDeep } from 'es-toolkit';
  import { computed, inject, provide, ref, useCssModule, watch } from 'vue';

  import type { SpacingDensities } from '../../../types/misc';
  import { MODULE_INJECTION } from '../Module/Module.keys';
  import Paginate from '../Paginate/Paginate.vue';
  import { DEFAULT_PAGE_SIZE } from './DataView.constants';
  import { DATA_VIEW_INJECTION } from './DataView.keys';
  import type {
    DataViewVariants,
    GoNextPage,
    GoPrevPage,
    SetPage,
    SortId,
    SortOrder,
    SortValue,
    UpdateCurrentFilters,
    UpdateCurrentSearch,
    UpdateCurrentSort,
    UpdateEvent,
  } from './DataView.types';
  import { deserializeSortValue, getNextSort } from './DataView.utils';

  export interface DataViewProps {
    /**
     * Can be used to provide the initial page.
     * Also, it can be used to control the active page with `v-model:current-page="myPage"`.
     */
    currentPage?: number;

    /**
     * Can be used to set the initial search term.
     * Also, it can be used to control the active search term with `v-model:current-search="mySearchTerm"`.
     */
    currentSearch?: string;

    /**
     * Can be used to provide the initial sort order.
     * Also, it can be used to control the active sort order with `v-model:current-sort="mySortOrder"`.
     */
    currentSort?: SortValue;

    /**
     * A list of records. If pagination is enabled, `data` must be only the current page of records.
     */
    data?: object[];

    /**
     * Controls the DataView's padding; the default value is "comfortable". On small screens, "compact" density is applied regardless of this prop's value.
     */
    density?: SpacingDensities;

    /**
     * The number of records to display per page.
     */
    pageSize?: number;

    /**
     * Opt-out of displaying pagination. Hides the pagination buttons and page stats.
     */
    disablePagination?: boolean;

    /**
     * Hides the bottom pagination without disabling the pagination from the toolbar
     */
    hideBottomPagination?: boolean;

    /**
     * The total number of records available.
     * Used to auto-enable pagination when results exist.
     * Use `Infinity` for activating 'n of many' pagination
     * DataView does not paginate data for you; instead, `props.data` should always show
     * the current page of results.
     */
    totalDataCount?: number;

    /**
     * Whether there is a next page of data or not as informed by the server.
     * This only applies when`totalDataCount="Infinity"` to determine if the "Next" pagination button should be disabled.
     */
    hasNextPage?: boolean;

    /**
     * DataView variant. The default value is `undefined`. Will default to `table` when used within
     * a `<Module variant="table">`.
     */
    variant?: DataViewVariants;

    isLoading?: boolean;
  }

  const props = withDefaults(defineProps<DataViewProps>(), {
    currentPage: 1,
    currentSearch: undefined,
    currentSort: undefined,
    data: () => [],
    density: 'comfortable',
    pageSize: DEFAULT_PAGE_SIZE,
    disablePagination: false,
    totalDataCount: 0,
    hasNextPage: undefined,
    hideBottomPagination: false,
    variant: undefined,
  });

  const emit = defineEmits<{
    (e: 'update', state: UpdateEvent): void;
    (e: 'update:currentFilters', newFilters: object): void;
    (e: 'update:currentPage', page: number, pageSize: number): void;
    (e: 'update:currentSearch', searchTerm?: string): void;
    (e: 'update:currentSort', payload?: SortValue): void;
  }>();

  const classes = useCssModule();
  const rootRef = ref<HTMLElement | null>(null);
  const moduleInjection = inject(MODULE_INJECTION.key, MODULE_INJECTION.defaults);
  const computedVariant = computed<DataViewVariants | undefined>(() => {
    if (props.variant) {
      return props.variant;
    }

    if (moduleInjection.variant?.value === 'table') {
      return 'table';
    }

    return undefined;
  });
  const isWithinModule = computed(() => moduleInjection.variant !== undefined);

  const includeBottomPaginationInModule = computed(() => moduleInjection.includeDataviewPagination?.value);

  // #region pagination
  const internalPage = ref(1);
  const isPaginationEnabled = computed(() => !props.disablePagination && props.totalDataCount > 0);
  const isPaginateNextDisabled = computed(() => props.totalDataCount === Infinity && !props.hasNextPage);
  const pageCount = computed(() => Math.ceil(props.totalDataCount / props.pageSize));

  const setPage: SetPage = function (page: number, { shouldEmit } = {}) {
    internalPage.value = page;

    if (shouldEmit) {
      emitUpdate();
      emit('update:currentPage', page, props.pageSize);
    }

    if (Number(rootRef.value?.getBoundingClientRect().top) < 0) {
      rootRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const goPrevPage: GoPrevPage = function ({ shouldEmit } = {}) {
    const page = Math.max(internalPage.value - 1, 1);

    setPage(page, { shouldEmit });
  };

  /** Updates the DataView internal state with the previous page */
  const goNextPage: GoNextPage = function ({ shouldEmit } = {}) {
    const page = Math.min(internalPage.value + 1, pageCount.value);

    setPage(page, { shouldEmit });
  };

  watch(
    () => props.currentPage,
    () => {
      setPage(props.currentPage);
    },
    { immediate: true },
  );
  // #endregion pagination

  // #region sorting
  const internalSort = ref<SortValue | undefined>(undefined);

  const internalSortId = computed<SortId | undefined>(() =>
    internalSort.value ? deserializeSortValue(internalSort.value).id : undefined,
  );
  const internalSortOrder = computed<SortOrder | undefined>(() =>
    internalSort.value ? deserializeSortValue(internalSort.value).order : undefined,
  );

  const updateCurrentSort: UpdateCurrentSort = function (sortId, { sortOrder, shouldEmit } = {}) {
    const { id: oldId, order: oldOrder } = deserializeSortValue(internalSort.value || '');
    const nextSort = getNextSort({ newId: sortId, newOrder: sortOrder, oldId, oldOrder });

    internalSort.value = nextSort;
    setPage(1);

    if (shouldEmit) {
      emitUpdate();
      emit('update:currentSort', nextSort);
    }
  };

  watch(
    () => props.currentSort,
    () => {
      updateCurrentSort(props.currentSort);
    },
    { immediate: true },
  );
  // #endregion sorting

  // #region search
  const internalSearch = ref('');

  const hasToolbar = ref(false);
  const isSelectable = ref(false);

  const updateCurrentSearch: UpdateCurrentSearch = function (searchTerm, { shouldEmit } = {}) {
    internalSearch.value = searchTerm || '';
    setPage(1);

    if (shouldEmit) {
      emitUpdate();
      emit('update:currentSearch', searchTerm);
    }
  };

  watch(
    () => props.currentSearch,
    () => {
      updateCurrentSearch(props.currentSearch);
    },
    { immediate: true },
  );
  // #endregion search

  // #region filters
  const internalFilters = ref<object>({});

  const updateCurrentFilters: UpdateCurrentFilters = function (newFilters: object, { shouldEmit } = {}) {
    internalFilters.value = newFilters;
    setPage(1);

    if (shouldEmit) {
      emitUpdate();
      emit('update:currentFilters', newFilters);
    }
  };
  // #endregion filters

  function emitUpdate() {
    emit('update', {
      page: internalPage.value,
      pageSize: props.pageSize,
      ordering: internalSort.value,
      search: internalSearch.value,
      filters: cloneDeep(internalFilters.value),
    });
  }

  provide(
    DATA_VIEW_INJECTION.key,
    Object.freeze({
      // Pagination
      currentPage: computed(() => internalPage.value),
      isPaginationEnabled,
      isPaginateNextDisabled,
      pageCount,
      pageSize: computed(() => props.pageSize),
      totalDataCount: computed(() => props.totalDataCount),
      setPage,
      goPrevPage,
      goNextPage,

      // Sorting
      currentSortId: internalSortId,
      currentSortOrder: internalSortOrder,
      updateCurrentSort,

      // Search
      currentSearch: computed(() => internalSearch.value),
      updateCurrentSearch,

      // Miscellaneous
      density: computed(() => props.density),
      hasToolbar,
      isEmpty: computed(() => props.data.length === 0),
      isSelectable,
      isLoading: computed(() => props.isLoading),
      isWithinModule,
      variant: computedVariant,
    }),
  );

  defineExpose({
    page: computed(() => internalPage.value),
    pageSize: computed(() => props.pageSize),
    setPage,
    goPrevPage,
    goNextPage,
    ordering: computed(() => internalSort.value),
    updateCurrentSort,
    search: computed(() => internalSearch.value),
    updateCurrentSearch,
    filters: computed(() => internalFilters.value),
    updateCurrentFilters,
  });
</script>

<template>
  <div ref="rootRef" class="stash-data-view relative" data-test="stash-data-view">
    <!-- @slot default -->
    <slot
      :current-page="internalPage"
      :current-sort-id="internalSortId"
      :current-sort-order="internalSortOrder"
      :data="data"
      :go-prev-page="goPrevPage"
      :go-next-page="goNextPage"
      :page-count="pageCount"
      :set-page="setPage"
      :total-data-count="props.totalDataCount"
      :update-current-sort="updateCurrentSort"
      :update-current-search="updateCurrentSearch"
    ></slot>

    <Paginate
      v-if="
        isPaginationEnabled &&
        (!isWithinModule || includeBottomPaginationInModule) &&
        pageCount > 1 &&
        !props.hideBottomPagination
      "
      class="mt-6"
      :class="classes.paginate"
      :current-page="internalPage"
      :is-next-disabled="isLoading || isPaginateNextDisabled"
      :list-length="props.totalDataCount"
      :page-size="props.pageSize"
      :variant="props.totalDataCount === Infinity ? 'stats' : 'standard'"
      @set-page="(page: number) => setPage(page, { shouldEmit: true })"
    />
  </div>
</template>

<style module>
  .paginate ul {
    margin: 0;
  }
</style>
